import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import LocationSelector from '../../components/common/Forms/LocationSelector';
import { getCountryName } from '../../utils/locationUtils';
import { Link, useNavigate } from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { updatePassword, signInWithEmailAndPassword, updateProfile as updateFirebaseProfile } from 'firebase/auth';
import { auth, db, storage } from '../../services/firebase';
import { profileAPI, academyAPI } from '../../services/api';
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import zxcvbn from 'zxcvbn';
import './Profile.css';
import { v4 as uuidv4 } from 'uuid';
function Profile() {
  const { currentUser, signOut: handleSignOut, reloadUser } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState({});
  const [editedData, setEditedData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const fileInputRef = useRef();
  const [applicationStatus, setApplicationStatus] = useState(null);
  // Password change states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Image cropping states
  const [upImg, setUpImg] = useState(null);
  const [crop, setCrop] = useState({
    unit: 'px',
    x: 0,
    y: 0,
    width: 0,
    height: 0
  });
  const [showCropper, setShowCropper] = useState(false);
  const [imgRef, setImgRef] = useState(null);

  useEffect(() => {
    if (currentUser?.uid) {
      fetchProfile();
      // Always fetch application status separately to use the custom claims
      fetchApplicationStatus();
    }
  }, [currentUser]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch directly from API without using cache
      const profileData = await profileAPI.getProfile({ includeSensitiveData: true });
      
      if (profileData) {
        setUserData(profileData);
        setEditedData(profileData);
        setImageUrl(profileData.profileImageUrl || null);
      } else {
        throw new Error('No profile data received');
      }
    } catch (error) {
      setError(error.message || 'Failed to load profile');
      setRetryCount(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  // Retry fetching if we have an error
  useEffect(() => {
    if (error && retryCount < 3) {
      const timer = setTimeout(() => {
        fetchProfile();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, retryCount]);

  const fetchApplicationStatus = async () => {
    try {
      // First check if user has applied (using custom claims)
      const statusCheck = await academyAPI.checkApplicationStatus();
      console.log('Application status check:', statusCheck);
      
      if (statusCheck.hasApplied) {
        // If they have applied, get the application details
        const academyApplication = await academyAPI.getApplication();
        console.log('Academy application details:', academyApplication);
        setApplicationStatus({
          hasApplied: true,
          applicationStatus: academyApplication
        });
      } else {
        setApplicationStatus({
          hasApplied: false,
          applicationStatus: null
        });
      }
    } catch (error) {
      console.error('Error fetching application status:', error);
      // Set default status if there's an error
      setApplicationStatus({
        hasApplied: false,
        applicationStatus: null
      });
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setMessage('');
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUpImg(e.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onImageLoad = (img) => {
    setImgRef(img);
    const { width, height } = img;
    setCrop({
      unit: 'px',
      x: 0,
      y: 0,
      width: Math.min(width, height),
      height: Math.min(width, height)
    });
  };

  const handleCropComplete = async () => {
    if (!imgRef || !crop.width || !crop.height) {
      return;
    }

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.naturalWidth / imgRef.width;
    const scaleY = imgRef.naturalHeight / imgRef.height;

    canvas.width = Math.floor(crop.width * scaleX);
    canvas.height = Math.floor(crop.height * scaleY);

    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      imgRef,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob((blob) => {
      if (!blob) {
        setError('Could not create image blob.');
        setShowCropper(false);
        setUpImg(null);
        if (upImg && upImg.startsWith('blob:')) {
          URL.revokeObjectURL(upImg);
        }
        return;
      }

      const croppedFile = new File([blob], 'profile_image.jpg', { type: 'image/jpeg' });
      setLoading(true); 
      
      // 1. Upload to Firebase Storage
      // Ensure currentUser and currentUser.uid are available
      if (!currentUser || !currentUser.uid) {
        setError('User not authenticated. Cannot upload image.');
        setLoading(false);
        setShowCropper(false);
        setUpImg(null);
        return;
      }
      const storagePath = `profile-images/${currentUser.uid}/${uuidv4()}_${croppedFile.name}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, croppedFile);

      uploadTask.on('state_changed',
        (snapshot) => {
          // Optional: handle progress
          // const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        },
        (storageError) => {
          setError('Failed to upload image to storage. Please try again.');
          setLoading(false);
          setShowCropper(false);
          setUpImg(null);
          if (upImg && upImg.startsWith('blob:')) {
            URL.revokeObjectURL(upImg);
          }
        },
        async () => {
          // 2. Get download URL
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // 3. Call the Cloud Function to update Firestore (and potentially other backend tasks)
            const result = await profileAPI.uploadProfileImage({ 
              fileUrl: downloadURL, 
              fileName: croppedFile.name 
            });

            if (result && result.success && result.imageUrl) {
              // 4. Update Firebase Auth user profile
              if (auth.currentUser) {
                await updateFirebaseProfile(auth.currentUser, { photoURL: result.imageUrl });
              }
              
              // 5. Update local state and context
              setImageUrl(result.imageUrl);
              setMessage('Profile image updated successfully.');
              setUserData(prev => ({...prev, profileImageUrl: result.imageUrl, photoURL: result.imageUrl }));
              setEditedData(prev => ({...prev, profileImageUrl: result.imageUrl, photoURL: result.imageUrl }));

              // Reload user data in AuthContext to reflect changes (like photoURL)
              if (reloadUser) {
                await reloadUser();
              }

            } else {
              throw new Error(result.message || 'Failed to update profile image via function.');
            }
          } catch (apiError) {
            setError(apiError.message || 'Failed to finalize image update. Please try again.');
          } finally {
            setLoading(false);
            setShowCropper(false);
            setUpImg(null);
            if (upImg && upImg.startsWith('blob:')) {
              URL.revokeObjectURL(upImg);
            }
          }
        }
      );
    }, 'image/jpeg', 1);
  };

  const handleDeleteImage = async () => {
    try {
      await profileAPI.deleteProfileImage({ uid: currentUser.uid });
      setImageUrl(null);
      setMessage('Profile image deleted');
    } catch (error) {
      setError('Failed to delete image');
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await profileAPI.updateProfile(editedData);
      
      // Update cache with new profile data
      // profileCache.set('profile', editedData);
      
      setUserData(editedData);
      setIsEditing(false);
      setMessage('Profile updated successfully');
    } catch (error) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    const { displayName = '', firstName = '', lastName = '' } = userData;
    if (displayName) {
      const names = displayName.split(' ');
      if (names.length > 1) {
        return `${names[0][0] || ''}${names[names.length - 1][0] || ''}`.toUpperCase();
      }
      return `${names[0][0] || ''}`.toUpperCase();
    }
    // Fallback if displayName is not available, though ideally it should be.
    return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'newPassword') {
      const score = zxcvbn(value).score;
      const levels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
      setPasswordStrength(levels[score]);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage('');
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (zxcvbn(passwordData.newPassword).score < 3) {
      setError('Please choose a stronger password');
      return;
    }
    
    try {
      setLoading(true);
      const currentUser = auth.currentUser;
      
      // Reauthenticate user with current password
      const credential = await signInWithEmailAndPassword(
        auth, 
        currentUser.email, 
        passwordData.currentPassword
      );
      
      // Update password
      await updatePassword(credential.user, passwordData.newPassword);
      
      setMessage('Password changed successfully');
      setShowPasswordChange(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength('');
    } catch (err) {
      if (err.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('Please sign out and sign in again before changing your password');
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChangeClick = () => {
    setShowPasswordChange(true);
    setError(null);
    setMessage('');
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordStrength('');
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="profile-container">
      <h2 className="profile-header">Your Profile</h2>
      {message && <div className="message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      {showCropper && upImg && (
        <div className="cropper-modal">
          <div className="cropper-container">
            <h3>Crop Image</h3>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => {
              }}
              aspect={1}
            >
              <img
                src={upImg}
                onLoad={(e) => onImageLoad(e.target)}
                alt="Crop me"
              />
            </ReactCrop>
            <div className="cropper-actions">
              <button onClick={handleCropComplete}>Confirm</button>
              <button onClick={() => {
                setShowCropper(false);
                setUpImg(null);
                if (upImg && upImg.startsWith('blob:')) {
                  URL.revokeObjectURL(upImg);
                }
              }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="profile-card">
        <div className="profile-image-container">
          {imageUrl ? (
            <img src={imageUrl} alt="Profile" className="profile-image" onClick={handleImageClick} />
          ) : (
            <div onClick={handleImageClick} className="profile-image default-avatar">
              {getInitials()}
            </div>
          )}
          <div className="image-overlay">
            <span className="image-action" onClick={handleImageClick}>Change</span>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageChange}
            accept="image/*"
            style={{ display: 'none' }}
          />
        </div>

        <div className="profile-details">
          {isEditing ? (
            <>
              <div className="form-group">
                <label>Display Name:</label>
                <input type="text" name="displayName" value={editedData.displayName || ''} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Date of Birth:</label>
                <input type="date" name="dateOfBirth" value={editedData.dateOfBirth || ''} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Institution:</label>
                <input type="text" name="institution" value={editedData.institution || ''} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Location:</label>
                <LocationSelector
                  countryCode={editedData.countryCode}
                  city={editedData.city}
                  onCountryChange={(code) => {
                    setEditedData(prev => ({
                      ...prev,
                      countryCode: code,
                      country: getCountryName(code),
                      city: ''
                    }));
                  }}
                  onCityChange={(cityName) => {
                    setEditedData(prev => ({ ...prev, city: cityName }));
                  }}
                />
              </div>
              <div className="form-group">
                <label>Phone Number:</label>
                <input type="tel" name="phoneNumber" value={editedData.phoneNumber || ''} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Occupation:</label>
                <input type="text" name="occupation" value={editedData.occupation || ''} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>About Me:</label>
                <textarea name="bio" value={editedData.bio || ''} onChange={handleInputChange} rows="4" className="bio-input" />
              </div>
            </>
          ) : (
            <>
              <p><strong>Name:</strong> {userData.displayName || (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : 'Not provided')}</p>
              <p><strong>Email:</strong> {userData.email || 'Not provided'}</p>
              <p><strong>Date of Birth:</strong> {userData.dateOfBirth || 'Not provided'}</p>
              <p><strong>Phone Number:</strong> {userData.phoneNumber || 'Not provided'}</p>
              <p><strong>Country:</strong> {userData.countryCode ? getCountryName(userData.countryCode) : (userData.country || 'Not provided')}</p>
              <p><strong>City:</strong> {userData.city || 'Not provided'}</p>
              <p><strong>Institution:</strong> {userData.institution || 'Not provided'}</p>
              <p><strong>Occupation:</strong> {userData.occupation || 'Not provided'}</p>
              <p><strong>Member Since:</strong> {userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : 'N/A'}</p>
              <p><strong>About Me:</strong></p>
              <p className="bio-text">{userData.bio || 'No bio provided'}</p>
            </>
          )}
        </div>

        <div className="profile-actions">
          {isEditing ? (
            <>
              <button onClick={handleSave} className="save-button">Save Changes</button>
              <button onClick={handleEditToggle} className="cancel-button">Cancel</button>
            </>
          ) : (
            <>
              <button onClick={handleEditToggle} className="profile-button edit">EDIT PROFILE</button>
              <button onClick={handlePasswordChangeClick} className="profile-button password">CHANGE PASSWORD</button>
              <button onClick={() => handleSignOut()} className="profile-button signout">SIGN OUT</button>
            </>
          )}
        </div>
      </div>

      {/* Academy Application Status Section */}
      {applicationStatus && applicationStatus.hasApplied && (
        <div className="academy-status-section">
          <div className="section-header">
            <h2>Academy Application</h2>
            <div className="section-divider"></div>
          </div>
          <div className="academy-status-card">
            {applicationStatus.applicationStatus ? (
              <div className="application-status-content">
                <div className="status-header">
                  <h3>Your Academy Application Status</h3>
                  <div className={`status-badge status-${applicationStatus?.applicationStatus?.status || 'pending'}`}>
                    <span className="status-icon">
                      {applicationStatus?.applicationStatus?.status === 'approved' ? '✓' : 
                       applicationStatus?.applicationStatus?.status === 'rejected' ? '✗' : 
                       applicationStatus?.applicationStatus?.status === 'waitlist' ? '⌛' : '⏳'}
                    </span>
                    <span className="status-text">
                      {applicationStatus?.applicationStatus?.status === 'approved' ? 'Approved' : 
                       applicationStatus?.applicationStatus?.status === 'rejected' ? 'Not Approved' : 
                       applicationStatus?.applicationStatus?.status === 'waitlist' ? 'Waitlisted' : 'Under Review'}
                    </span>
                  </div>
                </div>
                <div className="status-details">
                  <div className="detail-item">
                    <label>Submission Date:</label>
                    <span>{applicationStatus?.applicationStatus?.createdAt ? new Date(applicationStatus.applicationStatus.createdAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Last Updated:</label>
                    <span>{applicationStatus?.applicationStatus?.updatedAt ? new Date(applicationStatus.applicationStatus.updatedAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  {applicationStatus?.applicationStatus?.reviewDate && (
                    <div className="detail-item">
                      <label>Review Date:</label>
                      <span>{new Date(applicationStatus.applicationStatus.reviewDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {applicationStatus?.applicationStatus?.feedback && (
                    <div className="status-message">
                      <p>{applicationStatus.applicationStatus.feedback}</p>
                    </div>
                  )}
                </div>
                <div className="action-container">
                  <Link to="/academy" className="apply-button">
                    View Full Application <span className="arrow">→</span>
                  </Link>
                </div>
              </div>
            ) : (
              <div className="no-application">
                <h3>You have applied to the Mediterranean Academy for Youth</h3>
                <p>Your application is being processed. Please check back later for status updates.</p>
                <div className="action-container">
                  <Link to="/academy" className="apply-button">
                    View Application Status <span className="arrow">→</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showPasswordChange && (
        <div className="modal-overlay">
          <div className="password-change-modal">
            <div className="modal-header">
              <h3>Change Password</h3>
              <button 
                className="close-button"
                onClick={() => {
                  setShowPasswordChange(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setError(null);
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label>Current Password</label>
                <div className="password-input-container">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? "👁️" : "👁️‍🗨️"}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label>New Password</label>
                <div className="password-input-container">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? "👁️" : "👁️‍🗨️"}
                  </span>
                </div>
                {passwordData.newPassword && (
                  <p className="password-strength">Strength: {passwordStrength}</p>
                )}
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <span
                    className="password-toggle-icon"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                  </span>
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="save-button" disabled={loading}>
                  {loading ? 'Changing Password...' : 'Change Password'}
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordData({
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                    setError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
